import { FolderArchive, Send, X } from "lucide-react";

import { Button } from "@/components/button";
import { getExtensionFromFilename } from "@/utils/getExtensionFromMime.ts";

import type { SourceFileRow } from "../hooks/useSourceFileUploads";

import { FileRow } from "../components/FileRow";
import { SessionFileNameCombobox } from "../components/SessionFileNameCombobox";
import { UploadDropZone } from "../components/UploadDropZone";
import { useDcc } from "../contexts/DesignConfidenceCheckerProvider";
import { useDeleteConfirmations } from "../hooks/useDeleteConfirmations";

const SOURCE_FILE_ACCEPT =
  ".zip,.aep,.ai,.fig,.indd,.otf,.prproj,.psd,.psb,.ttf";

interface SourceUploadStepProps {
  onBack: () => void;
  onClose: () => void;
  onSubmit: () => void;
}

export function SourceUploadStep({
  onBack,
  onClose,
  onSubmit,
}: SourceUploadStepProps) {
  const { requestId, shell, sourceUploads } = useDcc();
  const files = sourceUploads.sourceFiles;
  const isUploading = sourceUploads.isUploading;
  const isSubmitting = shell.isSubmitting;
  const onAddFiles = sourceUploads.addFiles;
  const onDeleteAll = sourceUploads.reset;
  const onRemoveFile = sourceUploads.removeFile;
  const onUpdateFileMetadata = sourceUploads.updateFileMetadata;

  const { deleteConfirmationsModals, requestDelete, requestDeleteAll } =
    useDeleteConfirmations<SourceFileRow>({
      deleteAllText: "All uploaded source files will be removed.",
      deleteAllTitle: "Delete all files?",
      deleteOneText: "This source file will not be attached to the request.",
      deleteOneTitle: "Delete file?",
      onDeleteAll,
      onDeleteOne: (file) => onRemoveFile(file.clientId),
    });

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-start justify-between border-b border-border-secondary px-6 py-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border-secondary bg-bg-primary shadow-sm">
            <FolderArchive className="text-text-secondary" size={20} />
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-base font-semibold text-text-primary">
              Upload source files
            </span>
            <span className="text-sm text-text-tertiary">
              Your design evaluation is complete. Now, please provide the
              original source files.
            </span>
          </div>
        </div>
        <button
          aria-label="Close"
          className="rounded-md p-1.5 text-text-tertiary transition-colors hover:bg-bg-secondary"
          onClick={onClose}
          type="button"
        >
          <X size={20} />
        </button>
      </div>

      <div className="flex gap-4 p-6 flex-1 overflow-hidden">
        <UploadDropZone
          accept={SOURCE_FILE_ACCEPT}
          helperLine="Source files only (.ai, .zip, .psd, etc)."
          icon={<FolderArchive className="text-text-secondary" size={20} />}
          onFilesSelected={onAddFiles}
          warningLine="Do not upload evaluation images (JPEG/PNG) here."
        />

        <div className="flex flex-1 flex-col gap-2 overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-text-primary">
              List of uploaded source files
            </span>
            {files.length > 0 && (
              <Button
                color="gray"
                isDisabled={isUploading}
                onPress={requestDeleteAll}
                size="sm"
                variant="tertiary"
              >
                Delete all
              </Button>
            )}
          </div>

          {files.length === 0 ? (
            <p className="text-sm text-text-tertiary">No files uploaded yet</p>
          ) : (
            <div className="flex flex-col gap-3 overflow-y-auto pr-1">
              {files.map((file) => (
                <SourceUploadFileRow
                  file={file}
                  key={file.clientId}
                  onCancelUpload={() => onRemoveFile(file.clientId)}
                  onRemove={() => {
                    if (file.status === "error") {
                      onRemoveFile(file.clientId);

                      return;
                    }
                    requestDelete(file);
                  }}
                  onUpdateFileMetadata={onUpdateFileMetadata}
                  requestId={requestId}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end border-t border-border-secondary px-6 py-4">
        <div className="flex gap-3">
          <Button color="gray" onPress={onBack} size="lg" variant="secondary">
            Back
          </Button>
          <Button
            color="color"
            isDisabled={isSubmitting || isUploading}
            onPress={onSubmit}
            size="lg"
            variant="primary"
          >
            <Send size={14} />
            {isSubmitting ? "Submitting..." : "Submit designs"}
          </Button>
        </div>
      </div>

      {deleteConfirmationsModals}
    </div>
  );
}

function SourceUploadFileRow({
  file,
  onCancelUpload,
  onRemove,
  onUpdateFileMetadata,
  requestId,
}: {
  file: SourceFileRow;
  onCancelUpload: () => void;
  onRemove: () => void;
  onUpdateFileMetadata: ReturnType<
    typeof useDcc
  >["sourceUploads"]["updateFileMetadata"];
  requestId: number;
}) {
  const extension =
    getExtensionFromFilename(file.file.name).toUpperCase() || "FILE";

  return (
    <FileRow
      canCancel={file.status === "uploading"}
      canRemove={
        file.status === "attached" ||
        file.status === "error" ||
        file.status === "uploaded"
      }
      combobox={
        <SessionFileNameCombobox
          displayBaseName={file.displayBaseName}
          isDisabled={file.status === "uploading"}
          onChange={(value) => onUpdateFileMetadata(file.clientId, value)}
          originalFileName={file.file.name}
          previousVersionId={file.previousVersionId}
          requestId={requestId}
        />
      }
      extension={extension}
      onCancelUpload={onCancelUpload}
      onRemove={onRemove}
      progress={file.progress}
      showProgress={file.status === "uploading" && file.progress !== undefined}
      statusLabel={<SourceUploadStatusLabel file={file} />}
    />
  );
}

function SourceUploadStatusLabel({ file }: { file: SourceFileRow }) {
  if (file.status === "error") {
    return (
      <span className="truncate text-xs text-text-error-primary">
        {file.error ?? "Upload failed"}
      </span>
    );
  }

  return null;
}
